import * as React from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "~/components/ui/dialog";
import { Picture } from "~/components/ui/picture";
import type { ProductVideoContent } from "~/data/products/types";
import { wistiaPlayerSrc } from "~/lib/wistia";
import playIcon from "~/assets/products/drive-fleet/play.svg";

interface VideoFeatureSectionProps {
  content: ProductVideoContent;
}

// A poster with a headline and a "Watch Video" bar (Figma 10480:408072 mobile, 10480:408915
// desktop). Square-cornered portrait poster below md; from md (no tablet frame, so the same
// breakpoint the compatibility section widens at) the rounded landscape poster, with the
// larger desktop type from lg. The whole poster is one button that opens the clip, with
// sound, in a dialog.
export function VideoFeatureSection({ content }: VideoFeatureSectionProps) {
  const { t } = useTranslation("products");
  const dialogRef = React.useRef<HTMLDivElement>(null);

  return (
    <section className="bg-background py-10 lg:py-15">
      <div className="wrap flex justify-center">
        <Dialog>
          <DialogTrigger asChild>
            <button
              data-sid="fleet-video-play"
              type="button"
              aria-label={t(content.ariaLabel)}
              className="focus-visible:ring-bouncie-500 relative isolate flex h-[491px] w-full max-w-[320px] cursor-pointer flex-col justify-end gap-4 overflow-hidden pb-[30px] text-left focus-visible:ring-2 focus-visible:outline-none md:aspect-[924/392] md:h-auto md:max-w-[1155px] md:rounded-lg md:pb-4 lg:gap-9"
            >
              {content.poster ? (
                <Picture
                  src={content.poster}
                  mobileSrc={content.mobilePoster}
                  alt=""
                  className="absolute inset-0 -z-10 size-full object-cover"
                />
              ) : (
                <span aria-hidden className="absolute inset-0 -z-10 flex items-center justify-center bg-[var(--background-alt)]">
                  <span className="font-display text-[60px] leading-[1.1] font-extrabold tracking-[2.4px] text-[#f201ce]">FPO</span>
                </span>
              )}
              <span className="font-display text-text-heading px-6 text-xl leading-[1.15] font-extrabold lg:px-[32.5px] lg:text-[40px] lg:leading-[1.1]">
                {t(content.headline)}
              </span>
              <span className="flex h-16 w-[300px] max-w-full items-center gap-[30px] bg-[rgba(31,105,242,0.8)] px-6 lg:gap-5 lg:px-[32.5px]">
                <span className="font-display text-xl leading-[1.15] font-extrabold text-white">{t(content.ctaLabel)}</span>
                {/* eslint-disable-next-line local/no-raw-img -- decorative SVG icon */}
                <img src={playIcon} alt="" className="size-[46px]" />
              </span>
            </button>
          </DialogTrigger>
          <DialogContent
            ref={dialogRef}
            aria-describedby={undefined}
            // Radix would focus the iframe (its first focusable), and a cross-origin frame
            // swallows Esc. Focusing the dialog itself keeps Esc closing it.
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              dialogRef.current?.focus();
            }}
            className="max-w-4xl border-0 bg-black p-0 sm:rounded-lg [&>button]:text-white">
            <DialogTitle className="sr-only">{t(content.headline)}</DialogTitle>
            <div className="aspect-video w-full">
              <iframe
                src={wistiaPlayerSrc(content.wistiaId)}
                title={t(content.headline)}
                allow="autoplay; fullscreen"
                allowFullScreen
                className="size-full"
              />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
}
